import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<string|null>}
 */
const getUserWeddingId = async (userId) => {
  try {
    // Primero, obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError)
    }

    // Determinar el user_id de la boda:
    // - Si el usuario tiene un partner_user_id (es partner), buscar la boda del usuario principal (partner_user_id)
    // - Si el usuario NO tiene partner_user_id pero hay otro perfil que tiene este usuario como partner_user_id,
    //   buscar la boda del usuario mismo (es el usuario principal)
    // - Si no tiene partner, buscar la boda del usuario mismo
    let weddingOwnerId = userId
    
    if (profile?.partner_user_id) {
      // Este usuario es un partner, buscar la boda del usuario principal
      weddingOwnerId = profile.partner_user_id
    } else {
      // Verificar si hay otro perfil que tenga este usuario como partner
      // Si es así, este usuario es el principal y debe buscar su propia boda
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('partner_user_id', userId)
        .maybeSingle()
      
      // Si no hay partner que apunte a este usuario, buscar su propia boda
      weddingOwnerId = userId
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar 406

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching wedding ID:', error)
      return null
    }

    return data?.id || null
  } catch (error) {
    console.error('Error fetching wedding ID:', error)
    return null
  }
}

/**
 * Obtiene todos los items de presupuesto agrupados por categoría
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>}
 */
export const getBudgetItems = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return { categories: {}, items: [] }
    }

    const { data, error } = await supabase
      .from('budget_items')
      .select('*')
      .eq('wedding_id', weddingId)
      .order('category', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) throw error

    // Agrupar por categoría
    const categories = {}
    const items = data || []

    items.forEach(item => {
      const category = item.category || 'Uncategorized'
      if (!categories[category]) {
        categories[category] = {
          name: category,
          icon: item.category_icon || '',
          items: [],
          totalEstimated: 0,
          totalActual: 0,
          totalPaid: 0
        }
      }
      // Usar concept si existe, sino item_name
      const concept = item.concept || item.item_name || ''
      const itemWithConcept = { ...item, concept }
      categories[category].items.push(itemWithConcept)
      categories[category].totalEstimated += parseFloat(item.estimated_cost || 0)
      categories[category].totalActual += parseFloat(item.actual_cost || 0)
      // Usar paid si existe, sino verificar paid_amount > 0
      const isPaid = item.paid !== undefined ? item.paid : (parseFloat(item.paid_amount || 0) > 0)
      if (isPaid) {
        categories[category].totalPaid += parseFloat(item.actual_cost || item.estimated_cost || 0)
      }
    })

    return { categories, items }
  } catch (error) {
    console.error('Error fetching budget items:', error)
    return { categories: {}, items: [] }
  }
}

/**
 * Crea un nuevo item de presupuesto
 * @param {string} userId - ID del usuario
 * @param {Object} itemData - Datos del item
 * @returns {Promise<Object>}
 */
export const createBudgetItem = async (userId, itemData) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for this user. Please make sure you have a wedding set up.')
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('createBudgetItem - weddingId:', weddingId, 'userId:', userId)
    }

    // Validar que el concepto no esté vacío (permitir conceptos placeholder para categorías nuevas)
    if (itemData.concept === undefined || itemData.concept === null) {
      throw new Error('Expense concept is required.')
    }
    // Permitir conceptos vacíos solo si es un placeholder de categoría
    const concept = itemData.concept?.trim() || 'New category - Add your first expense'

    // Validar costos numéricos
    const estimatedCost = parseFloat(itemData.estimated_cost || 0)
    if (isNaN(estimatedCost)) {
      throw new Error('Estimated cost must be a valid number.')
    }
    if (estimatedCost < 0) {
      throw new Error('Estimated cost cannot be negative.')
    }

    let actualCost = null
    if (itemData.actual_cost !== null && itemData.actual_cost !== undefined && itemData.actual_cost !== '') {
      actualCost = parseFloat(itemData.actual_cost)
      if (isNaN(actualCost)) {
        throw new Error('Final cost must be a valid number.')
      }
      if (actualCost < 0) {
        throw new Error('Final cost cannot be negative.')
      }
    }

    const insertData = {
      wedding_id: weddingId,
      category: itemData.category || 'Uncategorized',
      item_name: concept, // Columna principal en la tabla
      estimated_cost: estimatedCost,
      actual_cost: actualCost,
      paid_amount: itemData.paid ? (actualCost || estimatedCost) : 0, // Usar paid_amount
    }
    
    // Agregar columnas opcionales si existen en la tabla
    if (itemData.category_icon !== undefined) {
      insertData.category_icon = itemData.category_icon || ''
    }
    
    // Agregar concept para compatibilidad si la columna existe
    insertData.concept = concept
    
    // Agregar paid para compatibilidad si la columna existe
    insertData.paid = itemData.paid || false

    if (process.env.NODE_ENV === 'development') {
      console.log('createBudgetItem - inserting:', insertData)
    }

    const { data, error } = await supabase
      .from('budget_items')
      .insert([insertData])
      .select()
      .single()

    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('createBudgetItem - Supabase error:', error)
      }
      console.error('Supabase error creating budget item:', error)
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating budget item:', error)
    throw error
  }
}

/**
 * Actualiza un item de presupuesto
 * @param {string} itemId - ID del item
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object>}
 */
export const updateBudgetItem = async (itemId, updates) => {
  try {
    const updateData = {}
    if (updates.category !== undefined) updateData.category = updates.category
    if (updates.category_icon !== undefined) updateData.category_icon = updates.category_icon
    if (updates.concept !== undefined) updateData.concept = updates.concept
    
    if (updates.estimated_cost !== undefined) {
      const estimatedCost = parseFloat(updates.estimated_cost || 0)
      updateData.estimated_cost = estimatedCost
    }
    
    if (updates.actual_cost !== undefined) {
      if (updates.actual_cost !== null && updates.actual_cost !== '') {
        const actualCost = parseFloat(updates.actual_cost)
        updateData.actual_cost = actualCost
      } else {
        updateData.actual_cost = null
      }
    }
    
    if (updates.paid !== undefined) updateData.paid = updates.paid

    const { data, error } = await supabase
      .from('budget_items')
      .update(updateData)
      .eq('id', itemId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error updating budget item:', error)
    throw error
  }
}

/**
 * Elimina un item de presupuesto
 * @param {string} itemId - ID del item
 * @returns {Promise<void>}
 */
export const deleteBudgetItem = async (itemId) => {
  try {
    const { error } = await supabase
      .from('budget_items')
      .delete()
      .eq('id', itemId)

    if (error) throw error
  } catch (error) {
    console.error('Error deleting budget item:', error)
    throw error
  }
}

/**
 * Elimina todos los items de una categoría
 * @param {string} userId - ID del usuario
 * @param {string} category - Nombre de la categoría
 * @returns {Promise<void>}
 */
export const deleteCategory = async (userId, category) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for user')
    }

    const { error } = await supabase
      .from('budget_items')
      .delete()
      .eq('wedding_id', weddingId)
      .eq('category', category)

    if (error) throw error
  } catch (error) {
    console.error('Error deleting category:', error)
    throw error
  }
}

/**
 * Obtiene el ID del usuario principal (el que tiene la boda)
 * @param {string} userId - ID del usuario actual
 * @returns {Promise<string|null>}
 */
const getPrincipalUserId = async (userId) => {
  try {
    // Obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError)
    }

    // Si el usuario tiene partner_user_id, es el partner, el principal es partner_user_id
    // Si no tiene partner_user_id, es el principal, el principal es userId
    if (profile?.partner_user_id) {
      return profile.partner_user_id
    }
    
    return userId
  } catch (error) {
    console.error('Error getting principal user ID:', error)
    return userId
  }
}

/**
 * Obtiene el presupuesto total (compartido entre principal y partner)
 * Ambos usuarios deben tener el mismo presupuesto en su columna budget
 * @param {string} userId - ID del usuario actual
 * @returns {Promise<number>}
 */
export const getUserBudget = async (userId) => {
  try {
    // Obtener el presupuesto del usuario actual primero
    const { data: currentProfile, error: currentError } = await supabase
      .from('profiles')
      .select('budget, partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (currentError && currentError.code !== 'PGRST116') {
      throw currentError
    }
    
    // Si el usuario tiene presupuesto, usarlo (debe estar sincronizado)
    if (currentProfile?.budget) {
      return parseFloat(currentProfile.budget)
    }
    
    // Si el usuario es partner y no tiene presupuesto, obtenerlo del principal
    if (currentProfile?.partner_user_id) {
      const { data: principalProfile, error: principalError } = await supabase
        .from('profiles')
        .select('budget')
        .eq('id', currentProfile.partner_user_id)
        .maybeSingle()

      if (principalError && principalError.code !== 'PGRST116') {
        throw principalError
      }
      
      // Si el principal tiene presupuesto, sincronizarlo en el partner
      if (principalProfile?.budget) {
        const budgetValue = parseFloat(principalProfile.budget)
        // Sincronizar en background (no esperar)
        supabase
          .from('profiles')
          .update({ budget: budgetValue })
          .eq('id', userId)
          .then(() => console.log('✅ Presupuesto sincronizado en partner'))
          .catch(err => console.warn('Error syncing budget:', err))
        
        return budgetValue
      }
    }
    
    return 0
  } catch (error) {
    console.error('Error fetching user budget:', error)
    return 0
  }
}

/**
 * Actualiza el presupuesto total del usuario principal y sincroniza con el partner
 * @param {string} userId - ID del usuario actual
 * @param {number} budget - Nuevo presupuesto
 * @returns {Promise<void>}
 */
export const updateUserBudget = async (userId, budget) => {
  try {
    // Obtener el ID del usuario principal
    const principalUserId = await getPrincipalUserId(userId)
    const budgetValue = parseFloat(budget)
    
    // Actualizar el presupuesto en el perfil del principal
    const { error: principalError } = await supabase
      .from('profiles')
      .update({ budget: budgetValue })
      .eq('id', principalUserId)

    if (principalError) throw principalError
    
    // CRÍTICO: Sincronizar el presupuesto con el partner (si existe)
    // Buscar si hay un partner enlazado al principal
    const { data: partnerProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('partner_user_id', principalUserId)
      .maybeSingle()
    
    if (partnerProfile) {
      // Actualizar el presupuesto en el perfil del partner también
      const { error: partnerError } = await supabase
        .from('profiles')
        .update({ budget: budgetValue })
        .eq('id', partnerProfile.id)
      
      if (partnerError) {
        console.warn('Error syncing budget to partner:', partnerError)
        // No lanzar error, el presupuesto del principal ya está actualizado
      } else {
        console.log('✅ Presupuesto sincronizado en principal y partner')
      }
    }
  } catch (error) {
    console.error('Error updating user budget:', error)
    throw error
  }
}

/**
 * Obtiene estadísticas del presupuesto
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>}
 */
export const getBudgetStats = async (userId) => {
  try {
    const totalBudget = await getUserBudget(userId)
    const { categories } = await getBudgetItems(userId)

    let totalEstimated = 0
    let totalActual = 0
    let totalPaid = 0

    Object.values(categories).forEach(category => {
      totalEstimated += category.totalEstimated
      totalActual += category.totalActual
      totalPaid += category.totalPaid
    })

    const totalPending = totalActual - totalPaid
    const remaining = totalBudget - totalActual
    const percentage = totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0

    return {
      totalBudget,
      totalEstimated,
      totalActual,
      totalPaid,
      totalPending,
      remaining,
      percentage
    }
  } catch (error) {
    console.error('Error fetching budget stats:', error)
    return {
      totalBudget: 0,
      totalEstimated: 0,
      totalActual: 0,
      totalPaid: 0,
      totalPending: 0,
      remaining: 0,
      percentage: 0
    }
  }
}
